import React, { createContext, useContext, useState, useEffect } from 'react';
import { DowntimeState, getAppState, VersionState } from '../services/api';
import packageJson from '../../package.json';

// Access version via the default import
const CLIENT_APP_VERSION = packageJson.version;

interface DowntimeContextType {
  downtime: DowntimeState;
  serverDowntime: DowntimeState;
  version: VersionState;
  showDowntimePreview: boolean;
  setShowDowntimePreview: React.Dispatch<React.SetStateAction<boolean>>;
}

const DowntimeContext = createContext<DowntimeContextType | undefined>(undefined);

interface DowntimeProviderProps {
  children: React.ReactNode;
}

const defaultDowntime: DowntimeState = {
  isDowntime: false,
  message: "",
  reason: "",
  resolution: null
};

export const DowntimeProvider: React.FC<DowntimeProviderProps> = ({ children }) => {
  const [serverDowntime, setServerDowntime] = useState<DowntimeState>(defaultDowntime);
  const [downtime, setDowntime] = useState<DowntimeState>(defaultDowntime);
  const [showDowntimePreview, setShowDowntimePreview] = useState<boolean>(false);
  const [version, setVersion] = useState<VersionState>({
    latest: CLIENT_APP_VERSION,
    minimum: CLIENT_APP_VERSION
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const serverAppState = await getAppState();
        if (!mounted) return;
        if (serverAppState) {
          setServerDowntime(serverAppState.downtime);
          setVersion(serverAppState.version);
        }
      } catch (e) {
        if (!mounted) return;
        console.error("Failed to fetch client version:", e);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const shouldShowDowntime = process.env.NODE_ENV !== "development" || showDowntimePreview;
    setDowntime({
      ...serverDowntime,
      isDowntime: shouldShowDowntime && serverDowntime.isDowntime
    });
  }, [serverDowntime, showDowntimePreview]);

  const value = {
    downtime,
    serverDowntime,
    version,
    showDowntimePreview,
    setShowDowntimePreview
  };

  return (
    <DowntimeContext.Provider value={value}>
      {children}
    </DowntimeContext.Provider>
  );
};

export const useDowntime = (): DowntimeContextType => {
  const context = useContext(DowntimeContext);
  if (context === undefined) {
    throw new Error('useDowntime must be used within a DowntimeProvider');
  }
  return context;
};
